/**
 * ChatPanel — text chat sidebar alongside (or outside of) a call.
 */

import React, { FormEvent, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../types';

interface Props {
  messages:       ChatMessage[];
  currentUserId:  string;
  recipientId:    string | null;
  callLogId?:     string;
  onSend:         (body: string) => void;
}

export const ChatPanel: React.FC<Props> = ({
  messages,
  currentUserId,
  recipientId,
  callLogId,
  onSend,
}) => {
  const [draft, setDraft]     = useState('');
  const bottomRef             = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || !recipientId) return;
    onSend(trimmed);
    setDraft('');
  };

  return (
    <div className="chat-panel">
      <div className="chat-panel__header">
        <h3>Chat {callLogId ? '(in-call)' : ''}</h3>
      </div>

      <div className="chat-panel__messages">
        {messages.length === 0 && (
          <p className="chat-panel__empty">No messages yet</p>
        )}
        {messages.map((msg) => {
          const isMine = msg.from_user_id === currentUserId;
          return (
            <div
              key={msg.id}
              className={`chat-panel__bubble ${isMine ? 'chat-panel__bubble--mine' : 'chat-panel__bubble--theirs'}`}
            >
              <p className="chat-panel__body">{msg.body}</p>
              <time className="chat-panel__time" dateTime={msg.ts}>
                {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </time>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form className="chat-panel__form" onSubmit={handleSubmit}>
        <input
          className="chat-panel__input"
          type="text"
          placeholder={recipientId ? 'Type a message…' : 'Select a user first'}
          value={draft}
          disabled={!recipientId}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          className="btn btn--send"
          type="submit"
          disabled={!draft.trim() || !recipientId}
        >
          Send
        </button>
      </form>
    </div>
  );
};
